/**
 * The auction form behind Create Auction (Super-admin/CreateAuction.js) and the Update Auction modal
 * (Super-admin/UpdateAuctionModal.js), kept pure so it can be tested on its own: what the form
 * holds, how each change moves it, what is checked before a save, and the body each save sends.
 * useAuctionForm runs it; each screen lays its fields out itself (AuctionFormFields.js).
 *
 * The two screens used to keep a copy each, and the copies had drifted: the modal kept the old
 * contest's teams when another contest was picked, sent a refused or unfinished logo upload as an
 * empty image (usp_CRUDAuction then cleared the stored one), and sent a cleared start date; Create
 * wiped the typed percentages whenever the number of winners changed, and took the sport id from a
 * sports list filtered by creator, sending "" - an automatic 400 - for a contest on another
 * account's sport. The bodies below keep the fields and value types each screen sent, so the server
 * reads them as before.
 */

import { prizeSplitPayload, prizeSplitProblem } from './prizeSplit';
import { readInstant } from './auctionFit';

/** The Bid Time Limit choices, in minutes. */
export const BID_TIME_OPTIONS = [0.5, 0.75, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * A bid time, stored in minutes, as every screen shows it: under a minute in seconds ("30 seconds",
 * "45 seconds"), otherwise in minutes ("1 minute", "2 minutes"). '' when there is none.
 *
 * @param {*} value The bid time: the API's number, or the select's string.
 * @returns {string}
 */
export const bidTimeLabel = (value) => {
    const minutes = Number(value);
    if (value === null || value === undefined || value === '' || !Number.isFinite(minutes)) return '';
    if (minutes < 1) {
        const seconds = Math.round(minutes * 60);
        return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
    }
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
};

/** What a save with a start date already behind it is told. */
export const PAST_START_MESSAGE =
    'Your selected start date and time has already expired. Please select a different time.';

/** What a save with a required field still empty is told. */
export const MISSING_FIELD_MESSAGE = 'field is required';

const splitList = (value) => (typeof value === 'string' ? value.split(',').map((part) => part.trim()) : []);

const placeTitle = (index) => `Place ${index + 1}`;

/** Create Auction as it opens. */
export const blankAuctionForm = () => ({
    mode: 'create',
    auctionId: 0,
    auctionName: '',
    // The selected contest's id as the select holds it, a string; '' while none is selected.
    tournamentId: '',
    tournamentName: '',
    sportId: '',
    sportName: '',
    teams: [],
    // Set when a contest is picked: its details, when they arrive, bring its teams.
    teamsPending: false,
    // The selected contest's details (Tournament/GetBy), for its start date.
    contestDetails: null,
    // A Date from the picker, the API's string for an auction not yet re-dated, or ''.
    startDate: '',
    bidTime: '',
    startingBid: '',
    bidIncrement: '',
    // Empty, not 0: an untouched Manager Hold is reported as missing rather than saved as 0%.
    xcuttaTake: '',
    // One entry per winning place, so their number is the winner count.
    winners: [],
    // The stored file name of the auction's logo.
    image: null,
    secondaryMarket: false,
    marketType: '',
    // Set by the first save, after which the fields show what they are missing.
    validated: false,
});

/**
 * The auction's teams from an Auctions list row. id stays a string because AuctionTeamOrder's
 * Draggable needs a string draggableId; the update body converts it. #173: the row's team lists
 * arrive in run order, and the order on screen is the order sent.
 *
 * @param {Object} row The list row.
 * @returns {{id: string, name: string, image: string}[]}
 */
export const teamsFromRow = (row) => {
    const teamIds = splitList(row?.teamId);
    const teamImages = splitList(row?.teamImages);
    return splitList(row?.teamNames).map((name, index) => ({ id: teamIds[index], name, image: teamImages[index] }));
};

/**
 * A contest's teams from its details (Tournament/GetBy): the order a new auction starts from.
 *
 * @param {Object} details The contest.
 * @returns {{id: string, name: string, image: string}[]}
 */
export const teamsFromContest = (details) => {
    const teamIds = splitList(details?.auctionTeams);
    const teamImages = splitList(details?.auctionTeamsImage);
    return splitList(details?.auctionTeamsName).map((name, index) => ({
        id: teamIds[index],
        name,
        image: teamImages[index],
    }));
};

/**
 * The Update Auction modal as it opens on a list row. Built when the modal opens, not when the
 * contest's details load: opening a second auction of the same contest does not refetch them, so the
 * previous auction's teams used to stay in the form and be saved onto this one.
 *
 * The winner places are seeded here and only here, once per opened auction (#146): an effect used to
 * re-seed them from the row on every change to the form, so percentages the manager had typed
 * snapped back to the stored ones as soon as another field was touched. They are sized to the row's
 * winner count, which a legacy row's lists need not match.
 *
 * @param {Object} row The list row.
 * @returns {Object} The form.
 */
export const auctionFormFromRow = (row) => {
    const storedCount = Number(row?.winnerCount) || 0;
    const storedTitles = splitList(row?.winnerTitle);
    const storedPercentages = splitList(row?.winnerPercentage);

    return {
        ...blankAuctionForm(),
        mode: 'edit',
        auctionId: row?.auctionId,
        auctionName: row?.auctionName ?? '',
        tournamentId: row?.tournamentId == null ? '' : String(row.tournamentId),
        tournamentName: row?.tournamentName ?? '',
        sportId: row?.sportId,
        sportName: row?.sportName ?? '',
        teams: teamsFromRow(row),
        startDate: row?.startDate ?? '',
        bidTime: row?.bidTime,
        startingBid: row?.startingBid,
        bidIncrement: row?.bidIncrement,
        xcuttaTake: row?.xcuttaTake,
        // A stored 0% stays 0 rather than turning blank.
        winners: Array.from({ length: storedCount }, (_, index) => ({
            title: storedTitles[index] || placeTitle(index),
            percentage: storedPercentages[index] ? Number(storedPercentages[index]) : '',
        })),
        image: row?.auctionImage ?? null,
    };
};

/** The places resized to count, keeping what was entered for the places that stay. */
const resizeWinners = (winners, count) =>
    Array.from({ length: count }, (_, index) => winners[index] ?? { title: placeTitle(index), percentage: '' });

/**
 * How the form changes.
 *
 * @param {Object} state The form.
 * @param {Object} action One of:
 *   field {name, value}; contestPicked {contest} - a contest list row, or null for none;
 *   contestLoaded {details}; teamsReordered {teams}; winnerCount {count};
 *   winnerPercentage {index, value}; secondaryMarket {on}; validated.
 * @returns {Object} The new form.
 */
export const auctionFormReducer = (state, action) => {
    switch (action.type) {
        case 'field':
            return { ...state, [action.name]: action.value };
        case 'contestPicked': {
            const contest = action.contest;
            if (!contest) {
                return {
                    ...state,
                    tournamentId: '',
                    tournamentName: '',
                    sportId: '',
                    sportName: '',
                    teams: [],
                    teamsPending: false,
                    contestDetails: null,
                };
            }
            // The teams come with the contest's details; until then there are none to order.
            return {
                ...state,
                tournamentId: String(contest.tournamentId),
                tournamentName: contest.tournamentName ?? '',
                sportId: contest.sportId,
                sportName: contest.sportName ?? '',
                teams: [],
                teamsPending: true,
                contestDetails: null,
            };
        }
        case 'contestLoaded': {
            // A reply for a contest no longer selected - picked and then changed - is dropped.
            if (String(action.details?.tournamentId) !== String(state.tournamentId)) return state;
            return {
                ...state,
                contestDetails: action.details,
                // Only a contest just picked takes its teams from the details; an auction being
                // edited keeps its own, in its run order.
                teams: state.teamsPending ? teamsFromContest(action.details) : state.teams,
                teamsPending: false,
            };
        }
        case 'teamsReordered':
            return { ...state, teams: action.teams };
        case 'winnerCount':
            return { ...state, winners: resizeWinners(state.winners, action.count) };
        case 'winnerPercentage':
            return {
                ...state,
                winners: state.winners.map((winner, index) =>
                    index === action.index ? { ...winner, percentage: action.value } : winner
                ),
            };
        case 'secondaryMarket':
            return { ...state, secondaryMarket: action.on, marketType: action.on ? state.marketType : '' };
        case 'validated':
            return { ...state, validated: true };
        default:
            return state;
    }
};

/**
 * The contests a save can name: the caller's own, as usp_CRUDAuction refuses any other (only the
 * contest's creator may add or edit its auctions). An auction being edited always offers its own
 * contest, even when the list does not hold it.
 *
 * @param {Array} contests The contest list (Tournament/GetAll).
 * @param {*} userId The signed-in user's id.
 * @param {Object} form The form.
 * @returns {Array} Contest list rows.
 */
export const selectableContests = (contests, userId, form) => {
    const own = (Array.isArray(contests) ? contests : []).filter((contest) => contest?.createdBy === userId);
    const listed = own.some((contest) => String(contest.tournamentId) === String(form.tournamentId));
    if (form.mode !== 'edit' || !form.tournamentId || listed) return own;

    return [
        { tournamentId: form.tournamentId, tournamentName: form.tournamentName, sportId: form.sportId, sportName: form.sportName },
        ...own,
    ];
};

/**
 * The date picker's value: a Date, or null when there is none.
 *
 * @param {*} value The form's start date (see readInstant).
 * @returns {Date|null}
 */
export const pickerDate = (value) => {
    const ms = readInstant(value);
    return Number.isFinite(ms) ? new Date(ms) : null;
};

/** Empty, or 0 - what the Create form has always treated as not filled in. */
const isUnset = (value) => String(value ?? '').trim() === '' || Number(value) === 0;

/**
 * Why the form cannot be saved yet, or null: a start date picked in the past, then the prize split
 * (#146), then a required field left empty.
 *
 * @param {Object} form The form.
 * @param {Object} prizeSplit From summarizePrizeSplit.
 * @param {string[]} winnerTitles The places' titles.
 * @param {Date} [now] The current time.
 * @returns {string|null}
 */
export const auctionFormProblem = (form, prizeSplit, winnerTitles, now = new Date()) => {
    // Only a date picked in this form: an auction re-saved with its stored date is the server's to
    // judge, against the contest as it is now.
    if (form.startDate instanceof Date && form.startDate < now) return PAST_START_MESSAGE;

    const splitProblem = prizeSplitProblem(prizeSplit, winnerTitles);
    if (splitProblem) return splitProblem;

    const missing =
        !String(form.auctionName ?? '').trim() ||
        !form.tournamentId ||
        !form.startDate ||
        isUnset(form.bidTime) ||
        (form.mode === 'create' && (isUnset(form.startingBid) || isUnset(form.bidIncrement)));
    return missing ? MISSING_FIELD_MESSAGE : null;
};

/**
 * The Auction/Add body: one auction, in a list. Only meaningful once auctionFormProblem is null.
 *
 * @param {Object} form The form.
 * @param {Object} prizeSplit From summarizePrizeSplit.
 * @returns {Object[]}
 */
export const createAuctionPayload = (form, prizeSplit) => {
    // The values that were checked are the values sent: "010" goes out as 10.
    const { xcuttaTake, winnerPercentage } = prizeSplitPayload(prizeSplit);

    return [
        {
            auctionId: 0,
            auctionName: form.auctionName,
            auctionImage: form.image || null,
            tournamentId: Number(form.tournamentId),
            sportId: String(form.sportId ?? ''),
            teamId: form.teams.map((team) => team?.id),
            startDate: form.startDate,
            startingBid: form.startingBid,
            bidIncrement: form.bidIncrement,
            xcuttaTake,
            winnerCount: form.winners.length,
            bidTime: form.bidTime,
            winnerTitle: form.winners.map((winner) => winner.title),
            winnerPercentage,
            // On the server IsPrivate means private: a private auction's resale listings are shown
            // only for sellers approved by its Manager. "Public" used to send true and "Private"
            // false, and secondaryMarket stayed true once the box had been ticked, even unticked.
            secondaryMarket: form.secondaryMarket,
            isPrivate: form.secondaryMarket && form.marketType === 'Private',
        },
    ];
};

/**
 * The Auction/Update body for one auction (AuctionUpdate puts it in a list). Only meaningful once
 * auctionFormProblem is null.
 *
 * secondaryMarket and isPrivate are deliberately left out: this form has no control for either, and
 * an omitted flag keeps its stored value in usp_CRUDAuction (WEB-P3-159). Starting Bid and Minimum
 * Bid Increment are not edited here either; the row's values go back.
 *
 * @param {Object} form The form.
 * @param {Object} prizeSplit From summarizePrizeSplit.
 * @returns {Object}
 */
export const updateAuctionPayload = (form, prizeSplit) => {
    // The values that were checked are the values sent. Number(xcuttaTake) || 0 used to send a
    // cleared Manager Hold as 0.
    const { xcuttaTake, winnerPercentage } = prizeSplitPayload(prizeSplit);

    return {
        auctionId: form.auctionId,
        auctionName: form.auctionName,
        auctionImage: form.image,
        tournamentId: String(form.tournamentId),
        sportId: Number(form.sportId),
        teamId: form.teams.map((team) => Number(team?.id)),
        startingBid: form.startingBid,
        bidIncrement: form.bidIncrement,
        startDate: form.startDate,
        xcuttaTake,
        bidTime: form.bidTime,
        winnerCount: form.winners.length,
        winnerTitle: form.winners.map((winner) => winner.title),
        winnerPercentage,
    };
};
